import { useEffect, useRef, useState } from 'react';

// Live mic-level VU meter (Alex 2026-06-26). Reads the SAME MediaStream that is sent
// to OpenAI, so it answers the one diagnostic question directly: do the bars dance
// when you speak (mic captures → problem is downstream) or stay flat (no capture →
// mic/permission/track problem)? Same VU-equalizer look as the STT recording meter.

const BARS = 7;

export function MicLevelMeter({ stream, active }: { stream: MediaStream | null; active: boolean }) {
  const [bands, setBands] = useState<number[]>(() => new Array(BARS).fill(0));
  const rafRef = useRef<number | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    if (!stream || !active) { setBands(new Array(BARS).fill(0)); return; }
    let cancelled = false;
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      const ctx = new Ctx();
      ctxRef.current = ctx;
      // iOS often starts the context suspended even after a gesture — resume it.
      void ctx.resume().catch(() => {});
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.6;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const SKIP = 2; // drop DC/sub-bass bins
      const per = Math.max(1, Math.floor((buf.length - SKIP) / BARS));
      const tick = () => {
        if (cancelled) return;
        analyser.getByteFrequencyData(buf);
        const out: number[] = [];
        for (let b = 0; b < BARS; b++) {
          let sum = 0;
          const s = SKIP + b * per;
          const e = Math.min(buf.length, s + per);
          for (let i = s; i < e; i++) sum += buf[i];
          out.push(Math.min(1, Math.sqrt(sum / Math.max(1, e - s) / 255) * 1.15));
        }
        setBands(out);
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch { /* AudioContext unsupported — meter just stays flat */ }
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      void ctxRef.current?.close().catch(() => {});
      ctxRef.current = null;
    };
  }, [stream, active]);

  return (
    <span className="voice-vu" aria-hidden title="Mikro-Pegel — tanzt, wenn dein Mikro Audio erfasst; flach = kommt nichts an">
      {bands.map((amp, i) => (
        <span key={i} className="voice-vu-bar" style={{ height: `${Math.max(12, Math.round(amp * 100))}%` }} />
      ))}
    </span>
  );
}
